// libraries
import React, { useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import OverviewTopPart from '/react4xp/components/OverviewTopPart'
import Button from '/react4xp/components/Button'
import RecipeCard from '/react4xp/components/RecipeCard'

// svg
import { UxArrowDown } from '/react4xp/components/Svg/Ux/Arrow'

// prop types
export interface RecipesOverviewWrapperProps {
	title?: string
	types?: {
		id?: string
		name?: string
		title?: string
	}[]
	endpoint?: string
	startValues?: {
		page?: number
		search?: string
		types?: string[]
		categories?: string[]
		pageSize?: number
	}
	strings?: {
		types?: string;
		categories?: string;
		search?: string
        searchButton?: string
        noResultsFound?: string
        loadMore?: string
	}
}

export default function RecipesOverviewWrapper({
	title,
	types,
	endpoint,
	startValues,
	strings
}: RecipesOverviewWrapperProps) {

	const [recipes, setRecipes] = useState<any[]>([])
    const [page, setPage] = useState(startValues?.page || 1)
    const [inputValue, setInputValue] = useState(startValues?.search || '')
	const [searchTerm, setSearchTerm] = useState(startValues?.search || '')
    const [selectedTypes, setSelectedTypes] = useState<any[]>(startValues?.types && types.filter((type)=> startValues.types.includes(type.name)) || [])
    const [hasNextPage, setHasNextPage] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)

	useEffect(() => {
        handleFetchRecipes()
	}, [searchTerm, selectedTypes, page])

    const handleFetchRecipes = async ()=> {
        
        if(!endpoint) return

		const isLoadMoreRequest = isLoadingMore

		if (isLoadMoreRequest) {
			setIsLoadingMore(true)
		} else {
			setIsLoading(true)
		}
        
        const origin = window.location.origin
        const url = new URL(`${origin}${endpoint}`)
        const queryParams = new URLSearchParams()

        if (page) queryParams.append('page', String(page))
        if (startValues?.pageSize) queryParams.append('pageSize', String(startValues?.pageSize))
        if (searchTerm) queryParams.append('search', searchTerm)
        if (selectedTypes.length) {
            selectedTypes.forEach(type => {
                queryParams.append('types', type.name)
            })
        }
    
        url.search = queryParams.toString()

		if (!isLoadMoreRequest) {
			updateCurrentUrl()
		}
        
        try {
            const response = await fetch(url)
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`)
            }
            const data = await response.json()
            
			setRecipes((prevRecipes) => {
				if (isLoadMoreRequest) {
					return [...prevRecipes, ...data.hits]
				}

				return data.hits
			})

            setHasNextPage(data.hasNextPage)

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error)
        }

		setIsLoading(false)
		setIsLoadingMore(false)
    }

    const handleSelectTypeFilter = (type) => {
        setPage(1)
        setSelectedTypes(prev => {
            const alreadySelected = prev.some(item => item.id === type.id)
            if (alreadySelected) {
                return prev.filter(item => item.id !== type.id)
            } else {
                return [...prev, { id: type.id, name: type.name }]
            }
        })
    }

	const handleSearchTerm = () => {
        setPage(1)
        setSearchTerm(inputValue)

        setTimeout(() => {
			const searchResultsElement = document.getElementById('search-results')
			if (searchResultsElement) {
				const headerOffset = 0
				const elementPosition = searchResultsElement.getBoundingClientRect().top + window.scrollY
				const offsetPosition = elementPosition - headerOffset
	
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})
			}
		}, 100)
	}
    
	const handleLoadMoreRecipes = () => {
        if(hasNextPage && !isLoadingMore){
			setIsLoadingMore(true)
            setPage((prevPage) => prevPage + 1)
        }
    }

    const updateCurrentUrl = () => {
        const origin = window.location.origin
        const updatedUrl = new URL(`${origin}${window.location.pathname}`)
        const queryParams = new URLSearchParams()

        if (searchTerm) queryParams.append('search', searchTerm)
        if (selectedTypes.length) {
            selectedTypes.forEach(type => {
                queryParams.append('types', type.name)
            })
        }

        updatedUrl.search = queryParams.toString()
        window.history.pushState({}, '', updatedUrl)
    
    }

	return (
		<main
			id='main'
			className='page-overview'
		>

			<OverviewTopPart
				title={title}
				filters={types}
				selectedFilters={selectedTypes}
				onFilterClick={handleSelectTypeFilter}
				search={{
					label: strings.search,
					searchButtonText: strings.searchButton,
					value: inputValue,
					isLoading,
					disabled: isLoading || isLoadingMore,
					onSearch: handleSearchTerm,
					onChange: (e) => {
						setInputValue(e.target.value)
					}
				}}
			/>

			<Section marginTop='none'>
				<div className='container'>

					<div id='search-results' className='overview-grid'>

						{recipes.length > 0 ? (
							<div className='overview-grid__wrapper'>
								{recipes.map((recipe, i) => (
									<RecipeCard
										key={i}
										href={recipe.url?.href}
                                        external={false}
                                        src={recipe.imageSkyr?.link} // 500px x 500px
                                        srcMedium={recipe.imageSkyrMedium?.link} // 400px x 400px
                                        alt={recipe.imageSkyr?.alt}
                                        width={400}
                                        sizes='(max-width: 992px) 100vw, 400px'
                                        title={recipe.title}
                                        time={recipe.preparation}
                                        //rating={recipe.rating}
                                        desc={recipe.intro}
									/>
								))}
							</div>
						) : (
							<div className='overview-grid__no-results'>
								<h2 className='h1-hero-small'>
									{strings.noResultsFound}
								</h2>
							</div>
						)}

						{hasNextPage && (
							<div className='overview-grid__load-more'>
								<Button
									isLoading={isLoadingMore}
									disabled={isLoadingMore}
									onClick={handleLoadMoreRecipes}
								>
									{strings.loadMore} <UxArrowDown />
								</Button>
							</div>
						)}

					</div>

				</div>
			</Section>
			
		</main>
	)
}
